import classNames from "classnames";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useHistory } from "react-router";

import { useTypedSelector } from "../../../../../../../store/reducers/use-typed-selector";
import { LanguagesAbbr, SessionPanelLayoutsTypes } from "../../../../../../../types/working-model";
import { useGetAdminUrl } from "../../../../../../../utils/admin-routing-utils";
import NavigationDropdown from "../../../../../../general-ui/dropdown/navigation-dropdown";
import Icon, { COLORS, ICONS } from "../../../../../../general-ui/icon";
import WaitingIndicator from "../../../../../../general-ui/waiting-indicator/waiting-indicator";
import useCreateModeratorRegistration from "../../../../../../moderation/use-create-moderator-registration";
import PanelHeaderOptions from "../../panel/components/panel-header-options";
import { QuestionsContext, QuestionsProvider } from "../../panel/contexts/engage/questions-state";
import { SessionPanelMap } from "../../panel/session-panel-route-map";
import { EngageHeader } from "../engage-header";
import { useBulkAnswer } from "../hooks/use-bulk-answer";
import { useQuestionPrompts } from "../hooks/question-prompts.hooks";
import { useQuestionSearch } from "../hooks/use-question-search";
import { InternalFilter } from "./components/question-card";
import QuestionList from "./components/question-list";
import QuestionSearchFilter from "./components/question-search-filter";
import { OptionalComponent } from "utils/optional-component";
import useClickAwayListener from "utils/use-click-away-listener";
import { usePageModule } from "../../panel/hooks/panel.hooks";
import { selectEditorNavV3 } from "@/store/selectors/feature-flags";
import { useLanguageParam } from "@/components/live-event/utils";
import useQuestionsSocketsAdmin from "@admin/create/session/navigation/engage/question-prompts/helpers/use-questions-sockets-admin";
import useOnIncomingSocketMessages from "./helpers/on-incoming-socket-messages";

import './question-prompt-view.scss';

const QuestionPromptView = ({ isCommentBox }: { isCommentBox?: boolean }) => {
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const moderation = useTypedSelector(state => state.CreateSessionReducer.workingSession?.moderation);
	const session_uuid = useTypedSelector(state => state.CreateSessionReducer.workingSession?.uuid);
	const publishUrl = useTypedSelector(state => state.CreateEventReducer.publishedUrl);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const isEditorV3 = useTypedSelector(selectEditorNavV3);
	const channel = useTypedSelector(state => state.AuthReducer.user?.active_channel);

	const languageParam = useLanguageParam();
	const language = (languageParam || 'en') as LanguagesAbbr;

	const [state, dispatch] = useContext(QuestionsContext);
	const pageModule = usePageModule();
	const adminPath = useGetAdminUrl();
	const routerHistory = useHistory();

	const isSingleLanguage = useMemo(() => {
		if (!workingEvent || !session_uuid) return true;
		return !!workingEvent.sessions.find(session => {
			return session.uuid === session_uuid && session.session_chat_single_language;
		});
	}, [session_uuid, workingEvent]);

	const handleError = useCallback((e: string) => {
		console.error(e);
	}, []);

	const { load, setFilter, search, next, hasMoreRef } = useQuestionPrompts(handleError);

	const {
		userSearchTerm,
		selectedFilter,
		hasActiveFilter,
		handleSearchChange,
		handleSearch,
		handleFilterChange,
	} = useQuestionSearch(search, setFilter);

	const onMessage = useOnIncomingSocketMessages(channel);

	useQuestionsSocketsAdmin({
		language,
		session_uuid: session_uuid || '',
		module: pageModule,
		singleLanguage: isSingleLanguage,
		onMessage,
	});

	const {
		panelRef,
		selecting,
		selectedQuestions,
		setSelectedQuestions,
		bulkAnswering,
		startSelecting,
		cancelSelecting,
		handleBulkAnswer,
	} = useBulkAnswer({ token, language, session_uuid, questions: state.questions, dispatch });

	const filteredQuestions = useMemo(
		() => state.questions.filter(q => InternalFilter(selectedFilter, q)),
		[state.questions, selectedFilter]
	);

	const engageMeta = useMemo(() => ({
		type: isCommentBox ? 'comment-box' : 'questions',
		icon: isCommentBox ? ICONS.COMMENT_FULL : ICONS.QUESTION_FILLED,
		label: isCommentBox ? "Comment Box" : "Question"
	}), [isCommentBox]);

	useEffect(() => {
		load();
	}, [load]);

	const registration = useCreateModeratorRegistration({
		activeEvent: workingEvent,
		waitForAdminUser: true,
		adminUser: user,
		propsToken: token,
		propsLanguage: language,
		propsRegistration: null,
		useCache: true
	});

	useClickAwayListener({
		ref: panelRef,
		onClickAway: () => null,
		onMousedownClickAway: () => cancelSelecting(),
		open: selecting
	});

	if (!publishUrl) {
		return (
			<div className="session-panel flex-center column">
				<h6 style={{ marginTop: -200, opacity: 0.75 }}>Event publish required</h6>
			</div>
		);
	}

	if (!registration && workingEvent?.registration_on) {
		return (
			<div className="session-panel flex-center column">
				<h6 style={{ marginTop: -200, opacity: 0.75 }}>Creating moderator registration...</h6>
				<br />
				<WaitingIndicator fillSpace={true} transparentFill={true} />
			</div>
		);
	}

	const showInitialLoader = state.loadingQuestions && !state.hasEverLoaded;
	const showEmptyState = state.questions.length === 0 && !hasActiveFilter && !state.loadingQuestions;

	return (
		<div className="question-panel-container" ref={panelRef}>
			<PanelHeaderOptions>
				<EngageHeader
					language={language}
					pageModule={pageModule}
					onBackClick={() => routerHistory.goBack()}
					engageMeta={engageMeta}
				/>
				<NavigationDropdown
					title={<Icon name={ICONS.THREE_DOTS_VERTICAL} size={12} color={COLORS.WHITE} />}
					className="session-panel-dropdown"
					isArrow={false}
					buttonClassName="session-panel-right-header-button round no-style"
					edge="right"
				>
					<>
						<button onClick={() => routerHistory.replace(adminPath({ path: SessionPanelMap[SessionPanelLayoutsTypes.BannedUsers] }))}>
							Banned users
						</button>
						<button onClick={startSelecting}>
							{selecting ? 'Stop Selecting' : 'Select Multiple'}
						</button>
					</>
				</NavigationDropdown>
			</PanelHeaderOptions>
			<div className={classNames("session-panel questions-view", { 'has-selections': selectedQuestions.length })}>
				{showInitialLoader ? (
					<div className={classNames("engage-empty-state", { 'editor-v3': isEditorV3 })}>
						<WaitingIndicator fillSpace={true} transparentFill={true} />
					</div>
				) : showEmptyState ? (
					<div className={classNames("engage-empty-state", { 'editor-v3': isEditorV3 })}>
						<h4>{isCommentBox ? "No comments yet" : "No questions yet"}</h4>
					</div>
				) : (
					<>
						<QuestionSearchFilter
							userSearchTerm={userSearchTerm}
							selectedFilter={selectedFilter}
							onChange={handleSearchChange}
							onSearch={handleSearch}
							onFilterChange={handleFilterChange}
						/>
						{filteredQuestions.length === 0 ? (
							<div className={classNames("engage-empty-state", { 'editor-v3': isEditorV3 })}>
								{state.loadingQuestions
									? <WaitingIndicator fillSpace={true} transparentFill={true} />
									: <h4>No results found</h4>
								}
							</div>
						) : (
							<QuestionList
								filteredQuestions={filteredQuestions}
								hasMoreRef={hasMoreRef}
								next={next}
								approvalRequired={moderation?.questions}
								selectedFilter={selectedFilter}
								selectedQuestions={selectedQuestions}
								setSelectedQuestions={setSelectedQuestions}
								selecting={selecting}
								isCommentBox={isCommentBox}
							/>
						)}
						<OptionalComponent display={selecting}>
							<div className="session-panel-footer">
								<button onClick={cancelSelecting}>Cancel</button>
								<button className="lemonade" onClick={handleBulkAnswer} disabled={bulkAnswering}>
									{bulkAnswering ? <WaitingIndicator /> : 'Mark as answered'}
								</button>
							</div>
						</OptionalComponent>
					</>
				)}
			</div>
		</div>
	);
};

const QuestionPrompts = ({ isCommentBox }: { isCommentBox?: boolean }) => {
	return (
		<QuestionsProvider>
			<QuestionPromptView isCommentBox={isCommentBox} />
		</QuestionsProvider>
	);
};

export default QuestionPrompts;
